import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, Loader2, UserPlus, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const inputCls =
  "w-full bg-white rounded-lg border border-[#D8E6F1] px-3 py-2.5 text-[#0C2A40] placeholder:text-[#93A5B5] focus:border-[#2880B8] focus:ring-2 focus:ring-[#2880B8]/20 focus:outline-none transition-all text-sm";

export default function EHR() {
  const [patients, setPatients] = useState([]);
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState([]);
  const [cc, setCc] = useState("");
  const [quick, setQuick] = useState("");
  const [generating, setGenerating] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [newP, setNewP] = useState({ name: "", dob: "", gender: "", insurance: "" });

  const loadPatients = async () => {
    const { data } = await api.get("/ehr/patients");
    setPatients(data);
    if (!selected && data.length) selectPatient(data[0]);
  };

  const selectPatient = async (p) => {
    setSelected(p);
    const { data } = await api.get(`/ehr/patients/${p.id}/notes`);
    setNotes(data);
  };

  useEffect(() => { loadPatients(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const addPatient = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/ehr/patients", newP);
      toast.success("Patient added");
      setAddOpen(false);
      setNewP({ name: "", dob: "", gender: "", insurance: "" });
      await loadPatients();
      selectPatient(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const runScribe = async (e) => {
    e.preventDefault();
    if (!selected) return;
    setGenerating(true);
    try {
      const { data } = await api.post("/ehr/scribe", { patient_id: selected.id, chief_complaint: cc, quick_notes: quick });
      setNotes((n) => [data, ...n]);
      setCc(""); setQuick("");
      toast.success("SOAP note generated by Astra AI Scribe");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tighter text-[#0C2A40]">EHR + AI Scribe</h1>
          <p className="mt-2 text-[#51667A]">Manage patients and generate structured SOAP notes in seconds.</p>
        </div>
        <button data-testid="ehr-add-patient" onClick={() => setAddOpen((o) => !o)} className="hidden md:flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#2880B8] text-white font-mono text-[10px] tracking-[0.15em] uppercase hover:bg-[#1F6EA1] transition-colors">
          <UserPlus size={14} /> Add Patient
        </button>
      </div>

      {addOpen && (
        <motion.form initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} onSubmit={addPatient} className="mt-6 bg-white rounded-2xl border border-[#D8E6F1] p-5 grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          <input data-testid="new-patient-name" required placeholder="Full name" value={newP.name} onChange={(e) => setNewP({ ...newP, name: e.target.value })} className={inputCls} />
          <input placeholder="DOB (YYYY-MM-DD)" value={newP.dob} onChange={(e) => setNewP({ ...newP, dob: e.target.value })} className={inputCls} />
          <input placeholder="Gender" value={newP.gender} onChange={(e) => setNewP({ ...newP, gender: e.target.value })} className={inputCls} />
          <input placeholder="Insurance" value={newP.insurance} onChange={(e) => setNewP({ ...newP, insurance: e.target.value })} className={inputCls} />
          <button data-testid="save-patient" type="submit" className="px-4 py-2.5 rounded-lg bg-[#0C2A40] text-white font-mono text-[10px] tracking-[0.15em] uppercase hover:opacity-90">Save</button>
        </motion.form>
      )}

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* Patient list */}
        <div className="lg:col-span-4 bg-white rounded-2xl border border-[#D8E6F1] p-4 h-fit">
          <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#93A5B5] px-2 mb-2">Patients ({patients.length})</p>
          <div className="space-y-1 max-h-[520px] overflow-y-auto">
            {patients.map((p) => (
              <button
                key={p.id}
                data-testid={`patient-${p.id}`}
                onClick={() => selectPatient(p)}
                className={`w-full text-left px-3 py-3 rounded-xl transition-colors ${selected?.id === p.id ? "bg-[#E7F1F8]" : "hover:bg-[#F2F8FC]"}`}
              >
                <div className="font-medium text-[#0C2A40]">{p.name}</div>
                <div className="font-mono text-[10px] tracking-[0.1em] uppercase text-[#93A5B5] mt-0.5">{p.mrn} · {p.insurance || "—"}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Detail + Scribe */}
        <div className="lg:col-span-8 space-y-5">
          {selected && (
            <div className="bg-white rounded-2xl border border-[#D8E6F1] p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="font-display text-2xl font-semibold text-[#0C2A40]">{selected.name}</h2>
                  <p className="font-mono text-[10px] tracking-[0.12em] uppercase text-[#93A5B5] mt-1">
                    {selected.mrn} · DOB {selected.dob || "—"} · {selected.gender || "—"} · {selected.insurance || "—"}
                  </p>
                </div>
                <span className="w-11 h-11 rounded-full bg-[#E7F1F8] flex items-center justify-center font-mono font-bold text-[#2880B8]">
                  {selected.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}
                </span>
              </div>

              <form onSubmit={runScribe} className="mt-6 border-t border-[#D8E6F1] pt-6">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles size={16} className="text-[#2880B8]" />
                  <span className="font-mono text-[11px] tracking-[0.15em] uppercase text-[#0C2A40]">Astra AI Scribe</span>
                </div>
                <input data-testid="scribe-cc" required placeholder="Chief complaint (e.g. Follow-up for hypertension)" value={cc} onChange={(e) => setCc(e.target.value)} className={inputCls} />
                <textarea data-testid="scribe-notes" rows={3} placeholder="Quick notes / dictation (e.g. BP controlled, no side effects, continue lisinopril)" value={quick} onChange={(e) => setQuick(e.target.value)} className={`${inputCls} mt-3`} />
                <button data-testid="scribe-generate" type="submit" disabled={generating} className="mt-4 flex items-center gap-2 px-6 py-3 rounded-full bg-[#2880B8] text-white font-mono text-[10px] tracking-[0.15em] uppercase hover:bg-[#1F6EA1] transition-colors disabled:opacity-60">
                  {generating ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Generate SOAP Note
                </button>
              </form>
            </div>
          )}

          {notes.map((n) => (
            <motion.div key={n.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} data-testid="soap-note" className="bg-white rounded-2xl border border-[#D8E6F1] p-6">
              <div className="flex items-center gap-2 mb-4">
                <FileText size={16} className="text-[#2880B8]" />
                <span className="font-display font-semibold text-[#0C2A40]">SOAP Note — {n.chief_complaint}</span>
                <span className="ml-auto font-mono text-[10px] text-[#93A5B5]">{new Date(n.created_at).toLocaleString()}</span>
              </div>
              {[["S", "Subjective", n.subjective], ["O", "Objective", n.objective], ["A", "Assessment", n.assessment], ["P", "Plan", n.plan]].map(([k, label, val]) => (
                <div key={k} className="flex gap-4 py-2 border-t border-[#F2F8FC] first:border-t-0">
                  <span className="font-mono font-bold text-[#2880B8] w-6 shrink-0">{k}</span>
                  <div>
                    <div className="font-mono text-[10px] tracking-[0.12em] uppercase text-[#93A5B5]">{label}</div>
                    <p className="text-sm text-[#0C2A40] leading-relaxed mt-1">{val}</p>
                  </div>
                </div>
              ))}
            </motion.div>
          ))}
          {selected && notes.length === 0 && (
            <div className="bg-[#F2F8FC] rounded-2xl border border-dashed border-[#D8E6F1] p-8 text-center text-[#93A5B5] font-mono text-xs uppercase tracking-[0.15em]">
              No notes yet — generate one with AI Scribe above.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
